







import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getBlogPostBySlug, getAuthorById } from '../services/apiService';
import type { BlogPost, Author } from '../types';
import Seo from '../components/shared/Seo';
import AuthorBio from '../components/blog/AuthorBio';
import TldrBox from '../components/blog/TldrBox';
import KeyFactsBox from '../components/blog/KeyFactsBox';
import PostContent from '../components/blog/PostContent';
import FaqSection from '../components/blog/FaqSection';
import CTABand from '../components/funnel/CTABand';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import HowToCite from '../components/blog/HowToCite';
import RelatedPosts from '../components/blog/RelatedPosts';
import { useData } from '../context/DataContext';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../components/ui/LocalizedLink';

import { generateArticleSchema, generateBreadcrumbSchema, generateFaqSchema } from '../lib/schema';

const BlogPostPage: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const { slug: rawSlug } = useParams<{ slug: string }>();
  // A single state to manage data: undefined = loading, null = not found, object = success.
  const [post, setPost] = useState<BlogPost | null | undefined>(undefined);
  const [author, setAuthor] = useState<Author | null | undefined>(undefined);
  const { firmInfo } = useData();

  // Clean the slug from react-router to remove any trailing slash.
  const slug = useMemo(() => rawSlug?.replace(/\/$/, ''), [rawSlug]);

  // Tell Netlify's Prerender (the crawler-facing headless render) when the article has really
  // rendered, so it snapshots the post and not the "Loading post..." placeholder.
  useEffect(() => {
    (window as any).prerenderReady = false;
    return () => { (window as any).prerenderReady = undefined; };
  }, [slug]);
  // The Related Posts block fetches its own list after the article renders. The crawler snapshot used to be
  // taken before it arrived, so crawlers never saw those links and each article looked like it had a single
  // inbound link (Semrush: "pages with only one internal link"). Hold the signal until they have loaded.
  const [relatedLoaded, setRelatedLoaded] = useState(false);
  useEffect(() => { setRelatedLoaded(false); }, [slug]);
  useEffect(() => {
    if (post === null || author === null || (post && relatedLoaded)) (window as any).prerenderReady = true;
  }, [post, author, relatedLoaded]);

  useEffect(() => {
    if (!slug) {
      return;
    }
    
    // 2026-10-04, found via a Semrush audit: this used to skip the data fetch for automated browsers
    // (navigator.webdriver) to avoid a React *hydration* mismatch. The app mounts with createRoot, not
    // hydrateRoot (index.tsx), so there is no hydration to mismatch -- and Netlify's Prerender extension
    // renders crawler requests in exactly such a browser, so every crawler and AI bot was being handed
    // "Loading post..." instead of the article, for every post on the site. Always fetch.

    // Reset state on slug change to show loading indicator
    setPost(undefined);
    setAuthor(undefined);

    const fetchData = async () => {
      console.log(`[DEBUG] BlogPostPage: Starting data fetch for slug: "${slug}"`);
      try {
        const postData = await getBlogPostBySlug(slug);
        console.log('[DEBUG] BlogPostPage: RAW API RESPONSE for getBlogPostBySlug:', postData);
        
        if (postData) {
          console.log(`[DEBUG] BlogPostPage: Post found. Fetching author with ID: "${postData.authorId}"`);
          const authorData = await getAuthorById(postData.authorId);
          console.log('[DEBUG] BlogPostPage: RAW API RESPONSE for getAuthorById:', authorData);
          
          setPost(postData);
          setAuthor(authorData);
        } else {
          console.warn(`[DEBUG] BlogPostPage: No post data returned for slug "${slug}". Setting state to null.`);
          setPost(null); // Explicitly set to null for "Not Found"
          setAuthor(null);
        }
      } catch (error) {
        console.error("[DEBUG] BlogPostPage: CRITICAL - An error occurred during data fetching:", error);
        setPost(null); // Set to null on error
        setAuthor(null);
      }
    };
    fetchData();
  }, [slug]);
  
  // On the server and on the initial client render, `post` will be `undefined`,
  // showing a consistent "Loading..." state and preventing hydration mismatches.
  if (post === undefined) {
    console.log('[DEBUG] BlogPostPage: Rendering "Loading..." state. This should match the server prerender.');
    return (
      <div className="text-center py-40">
        <h1 className="text-2xl font-bold">{t('blogPostPage.loading')}</h1>
      </div>
    );
  }

  // Once the fetch completes on the client, `post` will be either the data or `null`.
  if (!post || !author) {
    console.error(`[DEBUG] BlogPostPage: HYDRATION MISMATCH LIKELY. Rendering 'Not Found' page because post or author is falsy. If the prerendered page was "Loading...", this mismatch will cause a redirect loop.`, { post, author });
    return (
       <>
        <Seo title={t('blogPostPage.seoTitleNotFound')} noIndex={true} />
        <div className="text-center py-40">
            <h1 className="text-4xl font-bold">{t('blogPostPage.notFound')}</h1>
            <LocalizedLink to="/blog" className="mt-4 inline-block text-brand-gold-light hover:text-brand-gold">
            &larr; {t('blogPostPage.backToBlog')}
            </LocalizedLink>
        </div>
       </>
    );
  }

  console.log('[DEBUG] BlogPostPage: Proceeding to render with the following state:', { post, author });
  
  // A post exists in one language only; its canonical URL is that language's URL whichever path was requested.
  const postLang = post.language === 'es' ? 'es' : 'en';
  const canonicalUrl = `${firmInfo.url}${postLang === 'es' ? '/es' : ''}/blog/${slug}/`;
  // The content bot sets ogTitle equal to the title, which made <title> identical to the <h1>.
  const seoTitle = post.ogTitle && post.ogTitle !== post.title ? post.ogTitle : `${post.title} | The Action Firm`;

  const breadcrumbs = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.blog'), path: '/blog' },
    { name: post.title, path: `/blog/${slug}` }
  ];

  const safeFaqs = post.faqs || [];
  
  const articleSchema = generateArticleSchema(post, author, canonicalUrl);
  // 2026-10-05, found via a Semrush audit ("structured data that contains markup errors" on 17 posts): the
  // stored `schemaMarkup` on older posts is a dictionary of named schemas ({article, breadcrumb, faq, howTo,
  // localBusiness}) imported from the previous site, not a list of schema objects. It was being injected as one
  // untyped node, and its contents are wrong anyway (a 555 placeholder phone number, URLs for slugs that do not
  // exist, 2023 dates, a HowTo with no steps, a wp-content logo). Everything is generated from the post itself.
  const schemaPrefix = postLang === 'es' ? '/es' : '';
  const combinedSchema: object[] = [
    articleSchema,
    generateBreadcrumbSchema([
      { name: t('nav.home'), path: `${schemaPrefix}/` },
      { name: t('nav.blog'), path: `${schemaPrefix}/blog/` },
      { name: post.title, path: `${schemaPrefix}/blog/${slug}/` },
    ]),
    ...(safeFaqs.length > 0 ? [generateFaqSchema(safeFaqs)] : []),
  ];

  return (
    <>
      <Seo
        title={seoTitle}
        description={post.ogDescription || post.metaDescription}
        ogImageUrl={post.ogImageUrl || post.imageUrl}
        canonical={canonicalUrl}
        alternateLangs={[postLang]}
        schema={combinedSchema}
      />
      <div>
        <Breadcrumbs crumbs={breadcrumbs} />
        <div className="relative pt-20 pb-16">
          <img src={post.imageUrl} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-20" width="1440" height="960" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/80 to-transparent" />
          <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
            <h1 className="text-4xl md:text-6xl font-serif font-bold tracking-tight">{post.title}</h1>
            <p className="mt-6 text-white/70">
                {t('blogPostPage.published')} {new Date(post.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })} | 
                {t('blogPostPage.lastReviewed')} {new Date(post.lastReviewed).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="py-16">
          <div className="mx-auto max-w-3xl px-6 space-y-12">
            <div className="tldr-content"><TldrBox content={post.tldr} /></div>
            <KeyFactsBox facts={post.keyFacts || []} />
            <article className="prose prose-invert prose-lg text-white/90 prose-headings:font-serif prose-headings:text-white prose-a:text-brand-gold-light hover:prose-a:text-brand-gold prose-strong:text-white/90">
                <div className="prose-content"><PostContent htmlContent={post.content} /></div>
            </article>
            <AuthorBio author={author} />
            {safeFaqs.length > 0 && <FaqSection faqs={safeFaqs} />}
            <HowToCite post={post} canonicalUrl={canonicalUrl} />
          </div>
        </div>
        
        <RelatedPosts currentPostSlug={post.slug} onLoaded={() => setRelatedLoaded(true)} />

        <CTABand
          headline={t('blogPostPage.ctaTitle')}
          subheadline={t('blogPostPage.ctaSubtitle')}
          buttonText={t('blogPostPage.ctaButton')}
          buttonLink="/contact"
        />
      </div>
    </>
  );
};

export default BlogPostPage;